import { CalendarDays } from 'lucide-react';
import { useCallback, useId, useRef, useState, type ComponentPropsWithRef, type KeyboardEvent } from 'react';
import { assignRef } from '../assign-ref.ts';
import { CalendarPopup } from '../Calendar/CalendarPopup.tsx';
import { isCalendarDate, todayIn } from '../Calendar/calendar-month.ts';
import { usePlacementUnder } from '../popup-placement.ts';
import { useClosedOutside } from '../use-closed-outside.ts';
import styles from './Input.module.css';

export type DateInputProps = Omit<ComponentPropsWithRef<'input'>, 'type'> & { invalid: boolean };

const opensCalendar = (event: KeyboardEvent<HTMLInputElement>): boolean =>
  event.key === 'F4' || (event.altKey && event.key === 'ArrowDown');

const viewerLocale = (): string => document.documentElement.lang || navigator.language;

const typeIntoNativeInput = (input: HTMLInputElement, value: string): void => {
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const setValue = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  setValue?.call(input, value);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
};

const boundOf = (bound: string | number | undefined): string | undefined =>
  typeof bound === 'string' && isCalendarDate(bound) ? bound : undefined;

export const DateInput = ({
  invalid,
  ref,
  className,
  min,
  max,
  disabled,
  onKeyDown,
  ...rest
}: DateInputProps) => {
  const calendarId = useId();
  const native = useRef<HTMLInputElement | null>(null);
  const wrapper = useRef<HTMLSpanElement>(null);
  const [openedAt, setOpenedAt] = useState<string | null>(null);
  const open = openedAt !== null;
  const place = usePlacementUnder(wrapper, open);
  const attach = useCallback(
    (element: HTMLInputElement | null) => {
      native.current = element;
      assignRef(ref, element);
    },
    [ref],
  );
  const dismiss = useCallback(() => {
    setOpenedAt(null);
  }, []);
  useClosedOutside(wrapper, open, dismiss);

  const close = () => {
    setOpenedAt(null);
    native.current?.focus();
  };
  const openCalendar = () => {
    setOpenedAt(native.current?.value ?? '');
  };

  return (
    <span
      ref={wrapper}
      className={[styles['affixed'], styles['dateShell'], invalid ? styles['invalid'] : ''].join(' ')}
    >
      <input
        ref={attach}
        type="date"
        min={min}
        max={max}
        disabled={disabled}
        className={[styles['affixedInput'], styles['dateInput'], className ?? ''].join(' ')}
        aria-haspopup="dialog"
        aria-controls={open ? calendarId : undefined}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (event.defaultPrevented || disabled === true || !opensCalendar(event)) return;
          event.preventDefault();
          openCalendar();
        }}
        {...rest}
      />
      <button
        type="button"
        className={styles['calendarButton']}
        tabIndex={-1}
        aria-hidden="true"
        data-open={open}
        disabled={disabled}
        onClick={() => {
          if (open) dismiss();
          else openCalendar();
        }}
      >
        <CalendarDays size={16} aria-hidden="true" />
      </button>
      {openedAt === null ? null : (
        <CalendarPopup
          id={calendarId}
          value={isCalendarDate(openedAt) ? openedAt : ''}
          today={todayIn(new Date())}
          min={boundOf(min)}
          max={boundOf(max)}
          locale={viewerLocale()}
          place={{ ...place, width: undefined }}
          onPick={(date) => {
            if (native.current !== null) typeIntoNativeInput(native.current, date);
            close();
          }}
          onClose={close}
        />
      )}
    </span>
  );
};
